import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { Checkbox } from "@/components/ui/checkbox";

import type { CrmDataProvider } from "../providers/types";
import {
  GUARDIAS_KEY,
  HELP_CLASS,
  QueryBoundary,
  SectionCard,
  useGuardiasSave,
  type GuardiasSectionProps,
  type SectionAdvisor,
} from "./guardiasShared";

/**
 * "Asesoras del proyecto": the project-level 'Activa' flag used by the carousel. Editable by
 * the Team Leader of this project, direccion and a bpo-scope lead_manager; read-only for the
 * asesora. A plain alphabetical list: no counts, no comparison between advisors.
 */
export const GuardiasAdvisors = ({
  projectId,
  canEdit,
  advisors,
}: GuardiasSectionProps & { advisors: SectionAdvisor[] }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { saving, run } = useGuardiasSave();
  const flags = useQuery({
    queryKey: [GUARDIAS_KEY, projectId, "flags"],
    queryFn: () => dataProvider.getLeadAssignmentEligibleAdvisors(projectId),
  });

  return (
    <SectionCard
      title={translate("resources.guardias.advisors_title")}
      help={translate("resources.guardias.advisors_help")}
    >
      <QueryBoundary query={flags}>
        {(rows) =>
          advisors.length === 0 ? (
            <p className={HELP_CLASS}>
              {translate("resources.guardias.advisors_empty")}
            </p>
          ) : (
            <ul className="divide-y" data-testid="guardias-advisors">
              {advisors.map((advisor) => {
                const isActive = rows.some(
                  (row) =>
                    String(row.sales_id) === String(advisor.id) &&
                    row.is_eligible,
                );
                const id = `guardias-active-${advisor.id}`;
                return (
                  <li
                    key={advisor.id}
                    className="flex min-h-11 items-center justify-between gap-3 py-2"
                  >
                    <span className="min-w-0 truncate text-base">
                      {advisor.name}
                    </span>
                    <label
                      htmlFor={id}
                      className="flex min-h-11 shrink-0 items-center gap-2 text-base"
                    >
                      <Checkbox
                        id={id}
                        className="size-6"
                        checked={isActive}
                        disabled={!canEdit || saving}
                        aria-label={translate(
                          "resources.guardias.active_aria",
                          {
                            name: advisor.name,
                          },
                        )}
                        onCheckedChange={(checked) =>
                          run(() =>
                            dataProvider.setLeadAssignmentEligibility(
                              projectId,
                              advisor.id,
                              checked === true,
                            ),
                          )
                        }
                      />
                      {translate("resources.guardias.active_label")}
                    </label>
                  </li>
                );
              })}
            </ul>
          )
        }
      </QueryBoundary>
    </SectionCard>
  );
};
